"use client";

import { useCallback } from "react";
import type { MutableRefObject } from "react";
import type { PersistentDebugLogger } from "@/hooks/telemetry";
import { TEAM_MODAL_CENTER_ORIGIN, useTeamModal } from "@/contexts/team-modal";
import { runBuildYourOwnTeam } from "./homePageActions";
import type {
  HomePageInteractionState,
  HomePageModalStateLocal,
} from "./domain/home-page/types";

interface HomePageNewTeamHandlersLocal {
  /** Resolves once the gate is up — signed out, that is after the intent is
   *  parked. Handed straight to the CTA, which lets the DOM ignore it. */
  handleBuildYourOwnTeam: () => Promise<void>;
}

interface UseHomePageNewTeamHandlersInput {
  latestInteractionState: MutableRefObject<HomePageInteractionState>;
  modalDebugLogger: PersistentDebugLogger;
  modalState: Pick<HomePageModalStateLocal, "openAuthModal">;
}

/**
 * The landing's "Build Your Own" host handler.
 *
 * Build opens the ONE team modal on an EMPTY subject with edit invoked. The
 * modal owns the draft end to end — its discard boundary guards abandonment,
 * its save creates the record and lands the created team's regular state in
 * place — so the landing holds no build state at all: the logged-out branch
 * parks the intent on the one post-login carrier, and everything else is the
 * controller's.
 */
export function useHomePageNewTeamHandlers({
  latestInteractionState,
  modalDebugLogger,
  modalState,
}: UseHomePageNewTeamHandlersInput): HomePageNewTeamHandlersLocal {
  const { openAuthModal } = modalState;
  const { openNew } = useTeamModal();

  const handleBuildYourOwnTeam = useCallback(async (): Promise<void> => {
    await runBuildYourOwnTeam({
      interactionState: latestInteractionState.current,
      modalDebugLogger,
      openAuthModal,
      requestNewTeam: { openNew, origin: TEAM_MODAL_CENTER_ORIGIN },
    });
  }, [
    latestInteractionState,
    modalDebugLogger,
    openAuthModal,
    openNew,
  ]);

  return { handleBuildYourOwnTeam };
}
